import { RESUME_OUTCOME, type DriverResumeOutcome, type ResumeOutcome } from "@uploads";
import api from "@/api";
import {
	deleteFileUploadSession,
	getCompetingFileUploadOwnerLiveness,
	getFileUploadSessionById,
	isValidFileUploadOwnerId,
	type FileUploadSession
} from "@/db/fileUploadDb";
import { adminChunkedUploadDriver, isUploadLost, UploadCancelledError } from "@/services/chunkedUploadService";
import { createAdminResumeLeaseScope } from "@/services/uploads/adminChunkedUploadDriver";
import { adminUploadTelemetry } from "@/services/uploads/adminUploadTelemetry";
import { uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
import { hasNoServerUploadTarget } from "@/services/uploads/pendingUploadTarget";

export { RESUME_OUTCOME };
export type { ResumeOutcome };

function mapAdminResumeOutcome(session: FileUploadSession, outcome: DriverResumeOutcome): ResumeOutcome {
	if (outcome === "completed") return RESUME_OUTCOME.COMPLETED;
	if (outcome === "handed-off") return RESUME_OUTCOME.HANDED_OFF;
	if (outcome === "lost") return RESUME_OUTCOME.LOST;
	// A terminal refusal the driver already persisted as needs-attention (e.g. the persisted
	// byte-total contradiction). It is reported as its own outcome — never as "retry" — so the drain
	// surfaces "local data was preserved" instead of the generic retry-in-a-couple-minutes failure.
	if (outcome === "needs-attention") return RESUME_OUTCOME.NEEDS_ATTENTION;
	if (outcome === "recovery-blocked") return RESUME_OUTCOME.RECOVERY_BLOCKED;
	if (outcome === "retry" && session.status === "needs-attention") return RESUME_OUTCOME.NEEDS_ATTENTION;
	if (outcome === "retry" && session.status === "recovery-blocked") return RESUME_OUTCOME.RECOVERY_BLOCKED;
	if (outcome === "yielded" && session.status === "cancelled")
		throw new UploadCancelledError(`Upload of "${session.fileName}" was cancelled`);
	// R-1: "bytes-gone" is the ONE terminal the directive still allows — the retained buffers really
	// are missing or incomplete, so admin's byte-loss cleanup has just deleted the row. It used to be
	// bucketed with "retry", which announced "we'll keep retrying automatically" for a session that no
	// longer exists. LOST is the outcome whose copy is honest about a local copy we no longer have.
	if (outcome === "bytes-gone") return RESUME_OUTCOME.LOST;
	if (outcome === "retry" || outcome === "yielded") return RESUME_OUTCOME.RETRY;
	return assertNever(outcome);
}

/** The `buffers missing …` family the shared driver routes to its terminal bytes-gone verdict. */
function isMissingRetainedBytesError(error: unknown): boolean {
	return error instanceof Error && /buffers missing/i.test(error.message);
}

/**
 * How the DURABLE row's claim relates to the identity this resume was driving, resolved with the
 * same rigor `adminUploadLease` applies before it trusts a persisted owner:
 *
 *   ownerless                    — no owner cell at all (an orphan, or a deliberate hand-off row).
 *   invalid-successor-identity   — the owner cell exists but fails `isValidFileUploadOwnerId`, the
 *                                  lease's `invalid-persisted-owner-id` refusal. A corrupt owner is
 *                                  never evidence of a live successor.
 *   same-claim                   — the row still carries THIS resume's owner + claim pair.
 *   live-successor               — a different, valid owner holds the row and its liveness proof is
 *                                  not "dead" (the lease's `competing-owner-live` fence, where
 *                                  "indeterminate" also counts as live).
 *   indeterminate-successor      — a different, valid owner holds the row but the liveness probe
 *                                  itself failed, so death is unproven.
 *   dead-successor               — a different, valid owner holds the row and is provably dead; the
 *                                  drain's orphan scan owns returning that row to the pool.
 */
type LostRecoverySuccessorClaim =
	| "ownerless"
	| "invalid-successor-identity"
	| "same-claim"
	| "live-successor"
	| "indeterminate-successor"
	| "dead-successor";

async function resolveLostRecoverySuccessorClaim(
	session: FileUploadSession,
	persistedSession: FileUploadSession
): Promise<LostRecoverySuccessorClaim> {
	if (persistedSession.uploadOwnerId === undefined) return "ownerless";
	if (!isValidFileUploadOwnerId(persistedSession.uploadOwnerId)) return "invalid-successor-identity";
	// `claimsForeignOwner` in adminUploadLease, spelled the same way: the row is a SUCCESSOR's only
	// when its owner + claim pair is not the identity this resume was driving.
	if (persistedSession.uploadOwnerId === session.uploadOwnerId && persistedSession.uploadOwnerClaimId === session.uploadOwnerClaimId) {
		return "same-claim";
	}
	try {
		// `getCompetingFileUploadOwnerLiveness` (never the self-matching `getFileUploadOwnerLiveness`)
		// compared with `!== "dead"` is exactly the fence adminUploadLease uses to refuse a claim
		// against a competing owner.
		return (await getCompetingFileUploadOwnerLiveness(persistedSession)) !== "dead" ? "live-successor" : "dead-successor";
	} catch (livenessError) {
		console.warn("[Upload] Could not probe the successor claim's liveness after an upload-lost verdict", {
			sessionId: session.id,
			uploadId: session.uploadId,
			status: persistedSession.status,
			expectedOwnerClaimId: session.uploadOwnerClaimId,
			actualOwnerClaimId: persistedSession.uploadOwnerClaimId,
			...uploadFailureLogDetails(livenessError)
		});
		// An unreadable liveness probe is not proof the successor died, and R-1's rule is that a live,
		// owned session must never be reported as a terminal failure. Deferring costs one more paced
		// drain pass; rethrowing would spend a genuinely live upload's row on a red failure.
		return "indeterminate-successor";
	}
}

/**
 * R-1 ("there is no destroy", owner directive 2026-08-26). `recoverLostPersistedSession` reports a
 * successful reinitialization by RETHROWING the original `UPLOAD_LOST` error — the row it just
 * repointed at a fresh multipart target is deliverable again, but the resume call that discovered
 * the loss is over. Left unhandled that throw reaches the drain's generic error path, which is both
 * the wrong copy and the wrong bookkeeping for a session that is now simply restarting.
 *
 * Resolves the aftermath from the DURABLE row rather than from the error, because the same throw
 * covers several different states — reinitialized, deferred, yielded to a successor, bytes-gone, or
 * genuinely untouched — and each one gets its own log line (silent-failure rule).
 * Returns `undefined` when this error is not a lost-recovery aftermath at all, so the caller rethrows.
 */
async function resolveLostRecoveryOutcome(session: FileUploadSession, error: unknown): Promise<ResumeOutcome | undefined> {
	if (!isUploadLost(error) && !isMissingRetainedBytesError(error)) return undefined;
	let persistedSession: FileUploadSession | undefined;
	try {
		persistedSession = await getFileUploadSessionById(session.id);
	} catch (readError) {
		console.warn("[Upload] Could not read the session after an upload-lost verdict; leaving it to the retry drain", {
			sessionId: session.id,
			uploadId: session.uploadId,
			...uploadFailureLogDetails(readError)
		});
		return undefined;
	}
	if (!persistedSession) {
		console.warn("[Upload] Upload-lost recovery ended in the terminal bytes-gone discard", {
			sessionId: session.id,
			uploadId: session.uploadId,
			reason: error instanceof Error ? error.message : String(error)
		});
		return RESUME_OUTCOME.LOST;
	}
	if (persistedSession.uploadId !== session.uploadId) {
		console.info("[Upload] Upload-lost recovery reinitialized the session against a fresh multipart target", {
			sessionId: session.id,
			previousUploadId: session.uploadId,
			uploadId: persistedSession.uploadId,
			status: persistedSession.status
		});
		return RESUME_OUTCOME.RETRY;
	}
	if (persistedSession.status === "pending" || persistedSession.status === "queued") {
		console.warn("[Upload] Upload-lost recovery deferred the session with its bytes retained", {
			sessionId: session.id,
			uploadId: session.uploadId,
			status: persistedSession.status
		});
		return RESUME_OUTCOME.RETRY;
	}
	// `uploading` is the third live status a same-`uploadId` row can legitimately hold here, and the
	// one a refused reinitialization actually produces: `reinitializeSession` bails out BEFORE minting
	// (its `lease.owns` gate) or discards its mint after a declined CAS, then re-throws the original
	// UPLOAD_LOST — while `adminUploadLease`'s commit has written exactly `status: "uploading"` plus
	// the successor's own owner + claim fields, leaving the ORIGINAL uploadId in place. Nothing was
	// repointed, so the `uploadId !== uploadId` branch above cannot see it and this used to fall to
	// the rethrow below, where the drain reported a live, owned, fully-retained upload as a failure.
	//
	// The remaining four members of `FileUploadSessionStatus` — complete, cancelled, needs-attention,
	// recovery-blocked — are durable verdicts this aftermath resolver must not soften, so they keep
	// falling through to the rethrow, where `mapAdminResumeOutcome` and the cancel drain stay
	// authoritative. RETRY (not HANDED_OFF) is the deferral: admin's HANDED_OFF contract means "the
	// durable row is guaranteed gone" and would let the dialog's absent-row reconciliation flip a
	// successor's still-running upload to "complete".
	if (persistedSession.status === "uploading") {
		const successorClaim = await resolveLostRecoverySuccessorClaim(session, persistedSession);
		if (successorClaim === "live-successor" || successorClaim === "indeterminate-successor") {
			console.warn("[Upload] Upload-lost recovery yielded the session to a live successor claim; deferring instead of failing it", {
				sessionId: session.id,
				uploadId: session.uploadId,
				status: persistedSession.status,
				successorClaim,
				expectedOwnerClaimId: session.uploadOwnerClaimId,
				actualOwnerClaimId: persistedSession.uploadOwnerClaimId,
				ownerChanged: persistedSession.uploadOwnerId !== session.uploadOwnerId
			});
			return RESUME_OUTCOME.RETRY;
		}
		console.warn("[Upload] Upload-lost recovery left an `uploading` row with no live successor claim; surfacing the original failure", {
			sessionId: session.id,
			uploadId: session.uploadId,
			status: persistedSession.status,
			successorClaim,
			expectedOwnerClaimId: session.uploadOwnerClaimId,
			actualOwnerClaimId: persistedSession.uploadOwnerClaimId,
			ownerChanged: persistedSession.uploadOwnerId !== session.uploadOwnerId
		});
		return undefined;
	}
	console.warn("[Upload] Upload-lost recovery left the session untouched; surfacing the original failure", {
		sessionId: session.id,
		uploadId: session.uploadId,
		status: persistedSession.status
	});
	return undefined;
}

function assertNever(value: never): never {
	throw new Error(`Unexpected admin resume outcome: ${String(value)}`);
}

function warnInvalidCancelledResumeOwner(sessionId: string): void {
	try {
		console.warn(`[Upload] cancelled resume cleanup skipped for session ${sessionId} — invalid owner identity`);
	} catch {
		// A diagnostic failure must not change cancellation cleanup refusal.
	}
}

// AI review finding 3: the corrupt-identity refusal above is loud while this one — the row is gone, or its
// lease moved to a different valid owner — used to return silently, so triage could not tell a refused
// cleanup from "nothing to do".
function warnCancelledResumeCleanupSkipped(session: FileUploadSession, persistedSession: FileUploadSession | undefined): void {
	try {
		console.warn("[Upload] cancelled resume cleanup skipped", {
			sessionId: session.id,
			uploadId: session.uploadId,
			reason: persistedSession === undefined ? "row-gone" : "lease-moved",
			actualUploadId: persistedSession?.uploadId,
			actualOwnerClaimId: persistedSession?.uploadOwnerClaimId
		});
	} catch {
		// A diagnostic failure must not change cancellation cleanup refusal.
	}
}

export async function resumeAdminMultipartSession(
	session: FileUploadSession,
	onProgress: (bytesUploaded: number) => void,
	cancelSignal?: AbortSignal
): Promise<ResumeOutcome> {
	try {
		const outcome = await adminChunkedUploadDriver.resumePersistedMultipartSession({
			session,
			onProgress,
			cancelSignal,
			leaseScope: createAdminResumeLeaseScope(session.id, session.uploadOwnerClaimId)
		});
		return mapAdminResumeOutcome(session, outcome);
	} catch (error) {
		if (!(error instanceof UploadCancelledError)) {
			const lostRecoveryOutcome = await resolveLostRecoveryOutcome(session, error);
			if (lostRecoveryOutcome !== undefined) return lostRecoveryOutcome;
			throw error;
		}
		try {
			const persistedSession = await getFileUploadSessionById(session.id);
			const hasInvalidOwnerIdentity =
				(session.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(session.uploadOwnerId)) ||
				(persistedSession?.uploadOwnerId !== undefined && !isValidFileUploadOwnerId(persistedSession.uploadOwnerId));
			if (hasInvalidOwnerIdentity) warnInvalidCancelledResumeOwner(session.id);
			const stillOwnsPersistedUpload =
				!hasInvalidOwnerIdentity &&
				persistedSession !== undefined &&
				persistedSession.uploadId === session.uploadId &&
				persistedSession.uploadOwnerId === session.uploadOwnerId &&
				persistedSession.uploadOwnerClaimId === session.uploadOwnerClaimId;
			// LSP-21977: a `pending-target:` placeholder has no server upload the API can name — an abort for it
			// only 404s. Never requested: nothing remote to abort. Requested: it may own a server attempt this id
			// cannot name, so the row is left exactly where the failed abort used to leave it.
			const placeholder = stillOwnsPersistedUpload && hasNoServerUploadTarget(persistedSession);
			if (placeholder && persistedSession.targetInitiationStarted) {
				console.warn("[Upload] Cancelled resume left a placeholder whose target request already started", {
					sessionId: session.id,
					uploadId: session.uploadId
				});
			} else if (stillOwnsPersistedUpload) {
				if (placeholder) {
					// Nothing to abort; the local delete below is the whole cleanup.
				} else if (persistedSession.endpointFamily === "uploads") {
					const registryId = persistedSession.registryId ?? persistedSession.uploadId;
					const attemptId = persistedSession.attemptId ?? "";
					const generation = persistedSession.generation ?? 1;
					await (persistedSession.takeGeneration
						? api.abortUploadsUpload(registryId, attemptId, generation, "multipart", persistedSession.takeGeneration)
						: api.abortUploadsUpload(registryId, attemptId, generation, "multipart"));
				} else {
					await api.abortMultipartUpload(session.uploadId, session.s3Key, session.bucket, true);
				}
				await deleteFileUploadSession(session.id, session.uploadId, session.uploadOwnerId, session.uploadOwnerClaimId, {
					requireExactOwnerIdentity: true
				});
				console.info(`[Upload] Resuming "${session.fileName}" — cancelled and cleaned up`);
			} else if (!hasInvalidOwnerIdentity) {
				warnCancelledResumeCleanupSkipped(session, persistedSession);
			}
		} catch (cleanupError) {
			// The shared driver already tombstoned the session; the cancel drain retries cleanup.
			try {
				adminUploadTelemetry.logWarn(
					"recording.fileUpload.cleanupFailed",
					{ sessionId: session.id, uploadId: session.uploadId, fileName: session.fileName, operation: "abort-cancelled-resume" },
					cleanupError
				);
			} catch {
				// Cleanup telemetry must never replace the original cancellation.
			}
		}
		throw error;
	}
}
